/**
 * Council Trail Task 6.6 — PeekStripBlock.
 *
 * Off-Lens placeholder for a peer message: 12px indent + 2px persona-tinted
 * left border + plain text summary + expand affordance. Clicking expand
 * swaps in the full MessageBubble inline.
 *
 * Plan rev 2 surgical #15: dynamic Tailwind class interpolation
 * (`border-${color.light}`) is purged by Tailwind's JIT at build time;
 * those classes are never generated. We bind the persona tint via inline
 * `style.borderLeftColor` using the hexLight/hexDark fields added to
 * PALETTE in Task 5.2 + extended here.
 */
import React, { useState } from 'react';

import type { PeerMetadata } from '../../types/council';
import { getPersonaColor } from '../../utils/personaColor';
import { MessageBubble } from './MessageBubble';

export interface PeekStripBlockProps {
  /** Absent = OPERATOR off-trail strip (Lens SP1): neutral gray border, "+ You". */
  peerMetadata?: PeerMetadata;
  // Any MessageBubble-shaped message; the expand path passes it through.
  message: any;
  /** Optional extra props handed to MessageBubble when expanded. */
  bubbleProps?: Record<string, unknown>;
}

function _resolveBorderColor(peerMetadata: PeerMetadata): string {
  const color = getPersonaColor({
    id: peerMetadata.personaId,
    accent_color: peerMetadata.accentColor,
    name: peerMetadata.displayName,
  });
  // window.matchMedia is jsdom-safe (returns a stub) but defensive check anyway.
  const isDark = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return isDark ? color.hexDark : color.hexLight;
}

export function PeekStripBlock({ peerMetadata, message, bubbleProps }: PeekStripBlockProps) {
  const [expanded, setExpanded] = useState<boolean>(false);

  if (expanded) {
    return <MessageBubble message={message} {...(bubbleProps as any)} />;
  }

  if (!peerMetadata) {
    // Operator off-trail strip — neutral, no persona tint (the hydrating-gray).
    return (
      <div
        data-testid="peek-strip-block"
        style={{ borderLeftColor: '#9ca3af', borderLeftWidth: '2px', borderLeftStyle: 'solid' }}
        className="pl-3 py-1 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5"
      >
        <span data-testid="peek-strip-name">+ You</span>
        <button
          type="button"
          data-testid="peek-strip-expand"
          onClick={() => setExpanded(true)}
          className="rounded px-1 text-gray-500 dark:text-gray-400
                     hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label="Expand your message"
        >
          [+]
        </button>
      </div>
    );
  }

  const borderColor = _resolveBorderColor(peerMetadata);

  return (
    <div
      data-testid="peek-strip-block"
      data-persona-id={peerMetadata.personaId}
      style={{
        borderLeftColor: borderColor,
        borderLeftWidth: '2px',
        borderLeftStyle: 'solid',
      }}
      className="pl-3 py-1 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5"
    >
      <span data-testid="peek-strip-name">+ {peerMetadata.displayName}</span>
      <button
        type="button"
        data-testid="peek-strip-expand"
        onClick={() => setExpanded(true)}
        className="rounded px-1 text-gray-500 dark:text-gray-400
                   hover:bg-gray-100 dark:hover:bg-gray-800"
        aria-label={`Expand message from ${peerMetadata.displayName}`}
      >
        [+]
      </button>
    </div>
  );
}
